import React, {useRef, useState} from 'react';
import {IInputFieldProps} from '~/components/Form';
import {classByIntent} from '~/lib/intent';
import {endpoint} from '~/api';
import style from './index.module.scss';

type FileUpload = File | {
    name: string;
    url: string;
};

export interface IFileProps extends IInputFieldProps<FileUpload> {
    placeholder?: string;
}

export function FileInput({
    intent,
    value,
    placeholder,
    onChange,
    disabled
}: IFileProps) {
    const [file, setFile] = useState(value);
    const input = useRef<HTMLInputElement>(null);

    const onInputChange = ({target}: { target: HTMLInputElement }) => {
        if (target.files?.[0]) {
            const newFile = target.files[0];
            setFile(newFile);
            onChange?.(newFile);
        }
    };

    const saveBlob = (url: string, name: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const onDownload = async (fileUpload: FileUpload) => {
        const blob = fileUpload instanceof window.File
            ? fileUpload
            : await endpoint.get(fileUpload.url, {responseType: 'blob', baseURL: ''}).then(r => r.data as Blob);
        const url = window.URL.createObjectURL(blob);
        saveBlob(url, fileUpload.name);
        window.URL.revokeObjectURL(url);
    };

    const classes = [style.fileField, classByIntent(intent)];

    if (disabled) {
        classes.push(style.disabled);
    }

    return <div className={classes.join(' ')}>
        <div className={style.fileName}>
            {file ? file.name : <span className={style.fileEmpty}>{placeholder || 'No file chosen'}</span>}
        </div>
        <div className={style.fileButtons}>
            {file && <button type='button' className={style.fileButton} onClick={() => onDownload(file)}>
                <i className='fas fa-download'/>
            </button>}
            <button type='button' className={style.fileButton} onClick={() => !disabled && input.current?.click()}>
                <i className='fas fa-save'/>
            </button>
        </div>
        <input className={style.fileInput} ref={input} type='file' onChange={onInputChange}/>
    </div>;
}
